import React, { useState, useEffect } from 'react';
import './GuestDevicePage.css';
import GuestComplaintForm from './GuestComplaintForm';
import { DeviceIDBadge, CriticalityBadge } from './Badges';
import { AvailabilityImpactBadge } from './AvailabilityImpactBadge';
import { deviceHasAvailabilityImpact } from '../../constants/availabilityImpactFilter';
import GuestPageHeader from './GuestPageHeader';
import Footer from './Footer';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { applyOrgBranding } from '../../utils/orgBranding';

function GuestDevicePage({ device, onSuccess, onCancel }) {
  const [deviceData, setDeviceData] = useState(device);
  const [loading, setLoading] = useState(!device);
  const [error, setError] = useState(null);
  const [showComplaintForm, setShowComplaintForm] = useState(false);
  const [organizationName, setOrganizationName] = useState(null);
  const [locationName, setLocationName] = useState(null);

  // Fetch organization name when device data becomes available
  useEffect(() => {
    const fetchOrganizationName = async () => {
      try {
        // SECURITY: Get organization_id from device object
        // This ensures we're fetching the correct organization for multi-tenant isolation
        if (!deviceData || !deviceData.organization_id) {
          console.warn('No organization_id available in device data');
          return;
        }

        // Fetch organization name using the correct organization_id from device data
        const response = await fetch(API_ENDPOINTS.ORGANIZATIONS.GET(deviceData.organization_id));
        if (response.ok) {
          const data = await response.json();
          if (data && data.name) {
            setOrganizationName(data.name);
          }
          if (data?.branding) {
            applyOrgBranding(data.branding);
          }
        } else {
          console.warn('[GUEST_PAGE] Failed to fetch organization name:', response.statusText);
        }
      } catch (error) {
        console.error('[GUEST_PAGE] Error fetching organization name:', error);
        // Continue without organization name - not critical for guest view
      }
    };

    // Only fetch organization when device data is loaded
    if (deviceData) {
      fetchOrganizationName();
    }
  }, [deviceData]);

  useEffect(() => {
    // Device data provided directly by QRDeviceViewHandler - no additional loading needed
    if (device) {
      setDeviceData(device);
      setLoading(false);

      // Set location name directly from device data
      if (device.location_name) {
        setLocationName(device.location_name);
      }
    }
  }, [device]);

  if (showComplaintForm && deviceData && deviceData.device_status === 'active') {
    return (
      <GuestComplaintForm
        device={deviceData}
        locationName={locationName}
        onSuccess={onSuccess}
        onCancel={() => setShowComplaintForm(false)}
      />
    );
  }

  if (loading) {
    return (
      <div className="guest-device-view-container">
        <div className="loading-state">Loading device details...</div>
      </div>
    );
  }

  if (error || !deviceData) {
    return (
      <div className="guest-device-view-container">
        <div className="error-state">{error || 'Device not found'}</div>
      </div>
    );
  }

  const isInTransfer = deviceData.device_status === 'in_transfer';
  const canReportProblem = deviceData.device_status === 'active';
  const transferDest =
    deviceData.transferring_to_location_name || deviceData.transferring_to_department_name;
  const transferNotice = transferDest
    ? `Transferring to ${transferDest}`
    : 'Device transferring';

  return (
    <div className="guest-page-layout">
      {/* Header */}
      <GuestPageHeader organizationName={organizationName} locationName={locationName} />

      {/* Main Content */}
      <main className="guest-page-main guest-device-main">
        <div className="guest-device-view-container">
      {/* Header Section */}
      <div className="device-view-header">
        <div className="header-content">
          <div className="header-title-row">
            <h2>{deviceData.name || 'Unknown Device'}</h2>
            {deviceData.device_identifier && (
              <span className="device-id-badge-header">
                <DeviceIDBadge deviceId={deviceData.device_identifier} size="small" />
              </span>
            )}
          </div>
          {isInTransfer && (
            <span className="device-in-transfer-notice" role="status">
              {transferNotice}
            </span>
          )}
          <div className="header-badges">
            {deviceData.criticality && (
              <CriticalityBadge criticality={deviceData.criticality} size="small" />
            )}
            {deviceHasAvailabilityImpact(deviceData) && (
              <AvailabilityImpactBadge deviceHasImpact size="small" />
            )}
            <span className="header-department">
              <span className="department-icon-large" aria-hidden="true">
                {deviceData.department_icon || '🏢'}
              </span>
              <span className="department-badge-large">
                {deviceData.department || 'N/A'}
              </span>
            </span>
          </div>
        </div>
        <div className="header-actions">
          <div className="action-palette">
            <button
              type="button"
              className={`action-btn action-primary${!canReportProblem ? ' action-btn--disabled' : ''}`}
              onClick={() => {
                if (!canReportProblem) return;
                setShowComplaintForm(true);
              }}
              disabled={!canReportProblem}
              title={
                isInTransfer
                  ? 'Complaints cannot be created while this device is in a transfer'
                  : !canReportProblem
                    ? 'This device is not available for reporting right now'
                    : undefined
              }
            >
              <span className="action-icon">📋</span>
              <span className="action-text">Report a Problem</span>
            </button>
          </div>
        </div>
      </div>

      {/* Device Information Card */}
      <div className="device-view-content">
        <div className="info-card">
          <h3>Basic Device Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Device Name</span>
              <span className="info-value">{deviceData.name}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Model</span>
              <span className="info-value">{deviceData.model || 'Not available'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Serial Number</span>
              <span className="info-value">{deviceData.serial_number || 'Not available'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Manufacturer</span>
              <span className="info-value">{deviceData.manufacturer || 'Not available'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Physical Location</span>
              <span className="info-value">{deviceData.physical_location || 'Not available'}</span>
            </div>
          </div>
        </div>

        {/* Information Message */}
        <div className={`info-card guest-info-card${isInTransfer ? ' guest-info-card--transfer' : ''}`}>
          <h3>
            <span aria-hidden="true">
              {isInTransfer
                ? deviceData.transferring_to_department_name &&
                  !deviceData.transferring_to_location_name
                  ? '🔁'
                  : '🚚'
                : 'ℹ️'}
            </span>
            {isInTransfer
              ? transferDest
                ? `Transferring to ${transferDest}`
                : 'Device transferring'
              : 'Report an Issue'}
          </h3>
          <p className="info-card-text">
            {isInTransfer
              ? transferDest
                ? `This device is locked while transferring to ${transferDest}. Problem reporting will be available again once the transfer completes or is cancelled.`
                : 'This device is locked while transferring. Problem reporting will be available again once the transfer completes or is cancelled.'
              : 'Have you noticed an issue with this device? Please report it so our engineering team can investigate and resolve it promptly.'}
          </p>
        </div>
      </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default GuestDevicePage;
